import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api, { formatTRY } from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import OccupancyChart from "@/components/OccupancyChart";
import { BarChart3, TrendingUp, Wallet, BedDouble, XCircle, Users, LoaderCircle, Eye, MousePointerClick, Rocket } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const PERIODS = [
    { v: "7d", label: "Son 7 gün" },
    { v: "30d", label: "Son 30 gün" },
    { v: "90d", label: "Son 90 gün" },
    { v: "ytd", label: "Bu Yıl" },
];

const Kpi = ({ icon: Icon, label, value, hint, color = "emerald", testid }) => (
    <div data-testid={testid} className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
        <div className="flex items-start justify-between">
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
                <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white truncate">{value}</div>
                {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
            </div>
            <div className={`p-2 rounded-xl bg-${color}-100 dark:bg-${color}-950/40 text-${color}-600 dark:text-${color}-400 flex-shrink-0`}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
    </div>
);

const Bar = ({ value, max, color = "emerald" }) => (
    <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full bg-${color}-500 transition-all`} style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }} />
    </div>
);

export default function HotelStatsPage() {
    const { user } = useAuth() || {};
    const hotelId = user?.hotel_id;
    const [period, setPeriod] = useState("30d");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [traffic, setTraffic] = useState(null);

    useEffect(() => {
        if (!hotelId) return;
        setLoading(true);
        api.get(`/admin/hotels/${hotelId}/stats?period=${period}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));

        // Site traffic (last N days ≈ same window as period selector)
        const trafficDays = period === "7d" ? 7 : period === "30d" ? 30 : period === "90d" ? 90 : 30;
        api.get(`/hotel/analytics/overview?days=${trafficDays}`).then((r) => setTraffic(r.data)).catch(() => setTraffic(null));
    }, [hotelId, period]);

    if (loading || !data) {
        return <HotelLayout title="İstatistikler"><div className="flex items-center justify-center py-20"><LoaderCircle className="h-10 w-10 animate-spin text-emerald-500" /></div></HotelLayout>;
    }

    const { kpi, channels, daily, top_rooms } = data;
    const maxRev = Math.max(1, ...daily.map((d) => d.revenue));
    const maxChan = Math.max(1, ...channels.map((c) => c.revenue));
    const maxRoom = Math.max(1, ...top_rooms.map((r) => r.revenue));

    return (
        <HotelLayout title="İstatistikler" action={
            <select data-testid="stats-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-semibold">
                {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
        }>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 mb-6">
                <Kpi icon={Wallet} label="Toplam Gelir" value={formatTRY(kpi.total_revenue)} hint={`${kpi.total_bookings} rez.`} color="emerald" testid="stats-rev" />
                <Kpi icon={TrendingUp} label="ADR" value={formatTRY(kpi.adr)} hint="Ortalama günlük ücret" color="sky" testid="stats-adr" />
                <Kpi icon={BarChart3} label="RevPAR" value={formatTRY(kpi.revpar)} hint="Müsait oda başı gelir" color="violet" testid="stats-revpar" />
                <Kpi icon={BedDouble} label="Doluluk" value={`${kpi.occupancy_pct}%`} hint={`${kpi.total_room_nights} / ${kpi.available_room_nights} gece`} color="amber" testid="stats-occ" />
                <Kpi icon={Users} label="Ortalama Sepet" value={formatTRY(kpi.avg_booking_value)} color="rose" testid="stats-avg" />
                <Kpi icon={XCircle} label="İptal Oranı" value={`${kpi.cancel_rate_pct}%`} hint={`${kpi.cancel_count} iptal`} color="red" testid="stats-cancel" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {/* Channel breakdown */}
                <section className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <h2 className="font-bold text-slate-900 dark:text-white mb-4">Kanal Kırılımı</h2>
                    {channels.length === 0 ? <p className="text-sm text-slate-500">Veri yok.</p> : (
                        <div className="space-y-3">
                            {channels.map((c) => (
                                <div key={c.channel} className="space-y-1">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-semibold text-slate-700 dark:text-slate-200 capitalize">{c.channel}</span>
                                        <span className="text-slate-500 text-xs">{c.count} rez. · {formatTRY(c.revenue)}</span>
                                    </div>
                                    <Bar value={c.revenue} max={maxChan} color="sky" />
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Top rooms */}
                <section className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <h2 className="font-bold text-slate-900 dark:text-white mb-4">En Çok Satan Oda Tipleri (Top 5)</h2>
                    {top_rooms.length === 0 ? <p className="text-sm text-slate-500">Veri yok.</p> : (
                        <div className="space-y-3">
                            {top_rooms.map((r, i) => (
                                <div key={i} className="space-y-1">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-semibold text-slate-700 dark:text-slate-200">{r.name}</span>
                                        <span className="text-slate-500 text-xs">{r.count} rez. · {formatTRY(r.revenue)}</span>
                                    </div>
                                    <Bar value={r.revenue} max={maxRoom} color="emerald" />
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* Site Trafiği (my hotel) */}
            {traffic && (
                <section className="p-5 rounded-2xl bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-950/30 dark:to-indigo-950/30 border border-sky-200 dark:border-sky-900 mb-4">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <Rocket className="h-4 w-4 text-sky-600" /> Site Trafiği
                            </h2>
                            <p className="text-xs text-slate-500 mt-0.5">Otelinizin sayfasına gelen ziyaretçi ve rezervasyon dönüşümleri</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <Kpi icon={Eye} label="Görüntüleme" value={traffic.totals.views} color="sky" testid="traffic-views" />
                        <Kpi icon={MousePointerClick} label="Karttan Tıklama" value={traffic.totals.clicks} color="violet" testid="traffic-clicks" />
                        <Kpi icon={Users} label="Tekil Ziyaretçi" value={traffic.totals.unique_visitors} color="emerald" testid="traffic-visitors" />
                        <Kpi icon={TrendingUp} label="Rezervasyon Başlattı" value={traffic.totals.booking_starts} color="amber" testid="traffic-starts" />
                        <Kpi icon={Wallet} label="Tamamlanan" value={traffic.totals.booking_completes} color="rose" testid="traffic-completes" />
                    </div>
                    {traffic.by_day && traffic.by_day.length > 0 && (
                        <div className="h-56 bg-white/60 dark:bg-slate-900/60 rounded-xl p-3">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={traffic.by_day}>
                                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="views" name="Görüntüleme" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="clicks" name="Tıklama" stroke="#a855f7" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </section>
            )}

            {/* Occupancy chart */}
            {hotelId && (
                <div className="mb-4">
                    <OccupancyChart hotelId={hotelId} days={30} />
                </div>
            )}

            {/* Daily trend */}
            <section className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                <h2 className="font-bold text-slate-900 dark:text-white mb-4">Günlük Gelir Trendi</h2>
                {daily.length === 0 ? <p className="text-sm text-slate-500">Bu dönemde rezervasyon yok.</p> : (
                    <div className="flex items-end gap-1 h-40">
                        {daily.map((d) => (
                            <div key={d.date} className="flex-1 flex flex-col items-center group" title={`${d.date}: ${formatTRY(d.revenue)} (${d.bookings} rez.)`}>
                                <div className="w-full bg-gradient-to-t from-emerald-500 to-teal-400 rounded-t" style={{ height: `${(d.revenue / maxRev) * 100}%`, minHeight: d.revenue ? "2px" : "0" }} />
                            </div>
                        ))}
                    </div>
                )}
                <div className="mt-2 text-[10px] text-slate-500 flex justify-between">
                    <span>{daily[0]?.date || "—"}</span>
                    <span>{daily[daily.length - 1]?.date || "—"}</span>
                </div>
            </section>
        </HotelLayout>
    );
}
